import { getScrollTop, getScrollEventTarget } from '@theme/scroll-container';

function initHeader(header) {
  if (!header || header.dataset.absenHeaderReady) return;
  header.dataset.absenHeaderReady = 'true';
  const desktop = matchMedia('(min-width: 990px)');
  const prompt = header.querySelector('[data-absen-search-prompt]');
  const searchPrompts = [
    prompt?.dataset.promptPrimary,
    prompt?.dataset.promptSecondary,
  ];
  let promptIndex = 0;
  let promptTimer;
  const rotatePrompt = () => {
    clearInterval(promptTimer);
    if (prompt && desktop.matches && !document.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      promptTimer = setInterval(() => {
        promptIndex = (promptIndex + 1) % searchPrompts.length;
        if (!prompt.value && document.activeElement !== prompt) prompt.placeholder = searchPrompts[promptIndex];
      }, 4500);
    }
  };
  rotatePrompt();
  desktop.addEventListener('change', rotatePrompt);
  document.addEventListener('visibilitychange', rotatePrompt);
  const search = header.querySelector('.absen-header-search');
  const searchResults = search?.querySelector('.absen-header-search__results');
  const closeSearch = () => {
    searchResults.hidden = true;
    prompt.setAttribute('aria-expanded', 'false');
  };
  if (search && searchResults) {
    let searchCloseTimer;
    const openSearch = () => {
      clearTimeout(searchCloseTimer);
      if (!desktop.matches) return;
      searchResults.hidden = false;
      prompt.setAttribute('aria-expanded', 'true');
    };
    search.addEventListener('focusin', openSearch);
    search.addEventListener('input', openSearch);
    search.addEventListener('pointerdown', openSearch);
    search.addEventListener('pointerenter', () => clearTimeout(searchCloseTimer));
    search.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') searchCloseTimer = setTimeout(() => {
        if (!search.contains(document.activeElement)) closeSearch();
      }, 180);
    });
    search.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { closeSearch(); prompt.focus(); }
    });
    document.addEventListener('pointerdown', (event) => { if (!search.contains(event.target)) closeSearch(); });
    document.addEventListener('focusin', (event) => { if (!search.contains(event.target)) closeSearch(); });
    desktop.addEventListener('change', closeSearch);
    if (search.contains(document.activeElement)) openSearch();
  }

  const trigger = header.querySelector('[data-absen-header-catalog-trigger]');
  const catalogTemplate = header.querySelector('[data-absen-header-catalog-template]');
  const catalog = (catalogTemplate?.content || header).querySelector('[data-absen-header-catalog]');
  const topLeft = header.querySelector('.header__row--top .header__column--left');
  const bottomRow = header.querySelector('.header__row--bottom');
  if (!trigger || !catalog || !topLeft || !bottomRow) return;
  const marker = document.createComment('Category trigger position');
  trigger.before(marker);
  let closeTimer;
  let categoryScrollFrame;
  const stopCategoryScroll = () => {
    cancelAnimationFrame(categoryScrollFrame);
    categoryScrollFrame = null;
  };
  const setOpen = (open) => {
    if (open) {
      if (catalogTemplate?.isConnected) {
        catalogTemplate.replaceWith(catalogTemplate.content);
        updateMore();
      }
      const hero = document.querySelector('.ah2-hero__frame');
      const available = window.innerHeight - header.getBoundingClientRect().bottom - 24;
      catalog.style.setProperty('--absen-catalog-height', `${Math.max(260, Math.min(hero?.clientHeight || 560, available))}px`);
    }
    if (!open) stopCategoryScroll();
    catalog.hidden = !open;
    trigger.setAttribute('aria-expanded', String(open));
  };
  const open = () => { if (desktop.matches) { clearTimeout(closeTimer); setOpen(true); } };
  const close = () => { closeTimer = setTimeout(() => { if (!catalog.contains(document.activeElement) && document.activeElement !== trigger) setOpen(false); }, 160); };
  trigger.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') open(); });
  trigger.addEventListener('pointerleave', close);
  catalog.addEventListener('pointerenter', () => clearTimeout(closeTimer));
  catalog.addEventListener('pointerleave', close);
  trigger.addEventListener('click', open);
  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); open(); catalog.querySelector('a').focus(); }
  });
  header.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !catalog.hidden) { clearTimeout(closeTimer); setOpen(false); trigger.focus(); }
  });
  document.addEventListener('pointerdown', (event) => { if (!catalog.contains(event.target) && !trigger.contains(event.target)) setOpen(false); });
  document.addEventListener('focusin', (event) => { if (!catalog.contains(event.target) && !trigger.contains(event.target)) setOpen(false); });
  const categories = [...catalog.querySelectorAll('[data-absen-category]')];
  const panels = [...catalog.querySelectorAll('[data-absen-category-panel]')];
  const content = catalog.querySelector('[data-absen-catalog-content]');
  const rootPromos = [...catalog.querySelectorAll('[data-absen-root-promo]')];
  const roots = [...catalog.querySelectorAll('[data-absen-root]')];
  let switchTimer;
  const select = (key) => {
    categories.forEach((link) => {
      const active = link.dataset.absenCategory === key;
      link.classList.toggle('is-active', active);
      link.setAttribute('aria-expanded', String(active));
    });
    panels.forEach((panel) => { panel.hidden = panel.dataset.absenCategoryPanel !== key; });
    content.hidden = !key;
    rootPromos.forEach((promo) => { promo.hidden = true; });
    roots.forEach((root) => root.setAttribute('aria-expanded', 'false'));
    catalog.classList.toggle('is-expanded', Boolean(key));
  };
  categories.forEach((link) => {
    link.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(switchTimer);
      switchTimer = setTimeout(() => select(link.dataset.absenCategory), 120);
    });
    link.addEventListener('focus', () => { clearTimeout(switchTimer); select(link.dataset.absenCategory); });
    link.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault(); select(link.dataset.absenCategory);
        panels.find((panel) => !panel.hidden)?.querySelector('a')?.focus();
      }
    });
  });
  content.addEventListener('pointerenter', () => clearTimeout(switchTimer));
  roots.forEach((link) => {
    const showPromotion = () => {
      clearTimeout(switchTimer);
      select(null);
      const promo = rootPromos.find((item) => item.dataset.absenRootPromo === link.dataset.absenRoot);
      if (promo) {
        promo.hidden = false;
        catalog.classList.add('is-expanded');
        link.setAttribute('aria-expanded', 'true');
      }
    };
    link.addEventListener('pointerenter', showPromotion);
    link.addEventListener('focus', showPromotion);
    link.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showPromotion();
        rootPromos.find((item) => !item.hidden)?.querySelector('a')?.focus();
      }
    });
  });
  panels.forEach((panel) => {
    const links = [...panel.querySelectorAll('[data-absen-subcategory]')];
    const products = [...panel.querySelectorAll('[data-absen-products]')];
    const selectProducts = (key) => {
      links.forEach((link) => {
        const active = link.dataset.absenSubcategory === key;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-expanded', String(active));
      });
      products.forEach((group) => { group.hidden = group.dataset.absenProducts !== key; });
    };
    links.forEach((link) => {
      link.addEventListener('pointerenter', () => selectProducts(link.dataset.absenSubcategory));
      link.addEventListener('focus', () => selectProducts(link.dataset.absenSubcategory));
      link.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault(); products.find((group) => !group.hidden)?.querySelector('a')?.focus();
        }
      });
    });
  });
  const list = catalog.querySelector('[data-absen-catalog-scroll]');
  const more = catalog.querySelector('[data-absen-catalog-more]');
  const updateMore = () => {
    const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
    if (more.dataset.atBottom === String(atBottom)) return;
    more.dataset.atBottom = String(atBottom);
    more.querySelector('.absen-header-mega__more-label').textContent = atBottom ? more.dataset.backLabel : more.dataset.moreLabel;
    more.querySelector('[aria-hidden]').textContent = atBottom ? '↑' : '↓';
  };
  list.addEventListener('scroll', updateMore, { passive: true });
  more.addEventListener('pointerenter', (event) => {
    if (event.pointerType !== 'mouse' || !desktop.matches) return;
    stopCategoryScroll();
    let previousTime;
    const scrollCategories = (time) => {
      if (catalog.hidden) return stopCategoryScroll();
      if (previousTime !== undefined) list.scrollTop += Math.min(time - previousTime, 50) * .18;
      previousTime = time;
      if (list.scrollTop + list.clientHeight >= list.scrollHeight - 2) return stopCategoryScroll();
      categoryScrollFrame = requestAnimationFrame(scrollCategories);
    };
    categoryScrollFrame = requestAnimationFrame(scrollCategories);
  });
  more.addEventListener('pointerleave', stopCategoryScroll);
  more.addEventListener('pointerdown', stopCategoryScroll);
  more.addEventListener('click', () => {
    const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
    list.scrollTo({ top: atBottom ? 0 : list.scrollTop + list.clientHeight * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  });
  let scrollTarget;
  let frame;
  const update = () => {
    frame = null;
    const threshold = header.hasAttribute('data-absen-compact') ? 48 : 80;
    const compact = desktop.matches && getScrollTop() > threshold;
    if (header.hasAttribute('data-absen-compact') !== compact) {
      header.toggleAttribute('data-absen-compact', compact);
      if (compact) topLeft.append(trigger); else marker.after(trigger);
      bottomRow.inert = compact;
      setOpen(false);
    }
  };
  const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
  const bind = () => {
    scrollTarget?.removeEventListener('scroll', onScroll);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    if (!desktop.matches) setOpen(false);
    update();
  };
  bind();
  desktop.addEventListener('change', bind);
}
initHeader(document.querySelector('[data-absen-desktop-header]'));
document.addEventListener('shopify:section:load', (event) => initHeader(event.target.querySelector('[data-absen-desktop-header]')));
